import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import { 
  User, 
  Building, 
  Building2,
  Shield, 
  Key,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Eye,
  EyeOff,
  Save,
  Info,
  TestTube,
  XCircle,
  RefreshCw
} from '../components/Icons';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import toast from 'react-hot-toast';

const Settings = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [fbrToken, setFbrToken] = useState('');
  const [fbrTokenStatus, setFbrTokenStatus] = useState('not_configured');
  const [showToken, setShowToken] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [fbrMode, setFbrMode] = useState('validation');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [currentLogo, setCurrentLogo] = useState(null);
  // Invoice layout: 'classic' is the FBR document, 'basic' is the plain one for
  // businesses that are not on FBR digital invoicing.
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('classic');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [previewingTemplate, setPreviewingTemplate] = useState(null);
  const [previewInvoiceId, setPreviewInvoiceId] = useState(null);
  const [syncingData, setSyncingData] = useState(false);
  const [syncLogs, setSyncLogs] = useState([]);
  const [showSyncLogs, setShowSyncLogs] = useState(false);

  // Province list from database (static - will never change)
  const provinces = [
    'AZAD JAMMU AND KASHMIR',
    'BALOCHISTAN',
    'CAPITAL TERRITORY',
    'GILGIT BALTISTAN',
    'KHYBER PAKHTUNKHWA',
    'PUNJAB',
    'SINDH'
  ];

  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const [businessData, setBusinessData] = useState({
    business_name: '',
    business_type: '',
    address: '',
    city: '',
    state: '',
    country: 'Pakistan',
    tax_number: '',
    seller_ntn_cnic: '',
    seller_strn: '',
    seller_province: '',
    seller_business_name: '',
    seller_address: ''
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchTenantProfile();
    fetchFbrTokenStatus();
    fetchFbrMode();
    fetchCurrentLogo();
    fetchInvoiceTemplates();
  }, []);

  const fetchTenantProfile = async () => {
    try {
      const response = await apiGet('/api/tenant/profile');
      if (response.ok) {
        const data = response.data.tenant || response.data;
        setProfileData({
          name: data.name || '',
          email: data.contact_email || '',
          phone: data.contact_phone || ''
        });
        // Prioritize seller_address over address for FBR compliance
        const addressValue = data.seller_address || data.address || '';
        setBusinessData({
          business_name: data.seller_business_name || '',
          business_type: data.business_type || '',
          address: addressValue,
          city: data.city || '',
          state: data.state || '',
          country: data.country || 'Pakistan',
          tax_number: data.tax_number || '',
          seller_ntn_cnic: data.seller_ntn_cnic || '',
          seller_strn: data.seller_strn || '',
          seller_province: data.seller_province || '',
          seller_business_name: data.seller_business_name || '',
          seller_address: addressValue
        });
      }
    } catch (error) {
      console.error('Error fetching tenant profile:', error);
      toast.error('Failed to fetch profile');
    }
  };

  const fetchFbrTokenStatus = async () => {
    try {
      const response = await apiGet('/api/tenant/fbr-token');
      if (response.ok) {
        const data = response.data;
        // Set status based on has_token and overall status
        if (data.has_token) {
          setFbrTokenStatus('configured');
          if (data.token_preview) {
            // Don't overwrite the full token if user hasn't submitted yet
            // Only show preview
          }
        } else {
          setFbrTokenStatus('not_configured');
          setFbrToken('');
        }
      }
    } catch (error) {
      console.error('Error fetching FBR token status:', error);
      // Don't show error toast on initial load
    }
  };

  const fetchFbrMode = async () => {
    try {
      const response = await apiGet('/api/fbr/tenant/mode');
      if (response.ok) {
        setFbrMode(response.data.fbr_mode);
      }
    } catch (error) {
      console.error('Error fetching FBR mode:', error);
      // Don't show error toast for mode fetch failure
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiPut('/api/tenant/profile', profileData);
      if (response.ok) {
        toast.success('Profile updated successfully!');
      } else {
        toast.error(response.data.detail || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiPut('/api/tenant/business', businessData);
      if (response.ok) {
        toast.success('Business settings updated successfully!');
      } else {
        toast.error(response.data.detail || 'Failed to update business settings');
      }
    } catch (error) {
      console.error('Error updating business settings:', error);
      toast.error('Error updating business settings');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const response = await apiPost('/api/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      if (response.ok) {
        toast.success('Password updated successfully!');
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        toast.error(response.data.detail || 'Failed to update password');
      }
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error('Error updating password');
    } finally {
      setLoading(false);
    }
  };

  const handleFbrTokenUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await apiPut('/api/fbr/tenant/fbr-token', { 
        token: fbrToken,
        mode: fbrMode 
      });
      if (response.ok) {
        toast.success('FBR token and mode updated successfully!');
        setFbrTokenStatus('configured');
      } else {
        toast.error(response.data.detail || 'Failed to update FBR token');
      }
    } catch (error) {
      console.error('Error updating FBR token:', error);
      toast.error('Error updating FBR token');
    } finally {
      setLoading(false);
    }
  };

  const handleFbrModeChange = async (mode) => {
    setFbrMode(mode);
    try {
      const response = await apiPut('/api/fbr/tenant/mode', { mode });
      if (response.ok) {
        toast.success(`FBR mode changed to ${mode}`);
      } else {
        toast.error('Failed to update FBR mode');
      }
    } catch (error) {
      console.error('Error updating FBR mode:', error);
      toast.error('Error updating FBR mode');
    }
  };

  const handleUnsetToken = async () => {
    if (!window.confirm('Are you sure you want to unset the FBR token? This will disable FBR integration.')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await apiDelete('/api/fbr/tenant/remove-token');
      if (response.ok) {
        toast.success('FBR token removed successfully!');
        setFbrToken('');
        setFbrTokenStatus('not_configured');
      } else {
        toast.error(response.data.detail || 'Failed to remove FBR token');
      }
    } catch (error) {
      console.error('Error removing FBR token:', error);
      toast.error('Error removing FBR token');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const fetchInvoiceTemplates = async () => {
    try {
      const res = await apiGet('/api/tenant/invoice-templates');
      if (res.ok) {
        setTemplates(res.data.templates || []);
        setSelectedTemplate(res.data.selected || 'classic');
      }
      // Previews render a real invoice rather than dummy data, so the client
      // sees their own logo, buyer and items in the layout they are choosing.
      const inv = await apiGet('/api/invoices/?page=1&per_page=1');
      const rows = inv.ok ? (inv.data.invoices || inv.data.items || inv.data || []) : [];
      if (Array.isArray(rows) && rows.length > 0) setPreviewInvoiceId(rows[0].id);
    } catch (error) {
      console.error('Error fetching invoice templates:', error);
    }
  };

  const handleSelectTemplate = async (templateId) => {
    setSavingTemplate(true);
    try {
      const res = await apiPut('/api/tenant/invoice-template', { template: templateId });
      if (res.ok) {
        setSelectedTemplate(templateId);
        toast.success('Invoice template updated');
      } else {
        toast.error(res.data?.detail || 'Could not update invoice template');
      }
    } catch (error) {
      toast.error('Could not update invoice template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handlePreviewTemplate = async (templateId) => {
    if (!previewInvoiceId) {
      toast.error('Create an invoice first, then you can preview it in each template');
      return;
    }
    setPreviewingTemplate(templateId);
    try {
      // The PDF endpoint needs the bearer token, so it cannot simply be opened
      // in a new tab; fetch it and hand the browser a blob instead.
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || ''}/api/invoices/${previewInvoiceId}/generate-pdf?template=${templateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('preview failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error('Could not generate the preview');
    } finally {
      setPreviewingTemplate(null);
    }
  };

  const fetchCurrentLogo = async () => {
    try {
      const response = await apiGet('/api/tenant/logo');
      if (response.ok && response.data.has_logo) {
        setCurrentLogo(response.data.logo_url);
      }
    } catch (error) {
      console.error('Error fetching current logo:', error);
      toast.error('Failed to fetch current logo');
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', logoFile);
      
      const response = await apiPost('/api/tenant/logo', formData);
      
      if (response.ok) {
        toast.success('Logo uploaded successfully!');
        setLogoFile(null);
        setLogoPreview(null);
        // Fetch the new logo to display
        await fetchCurrentLogo();
      } else {
        toast.error(response.data.detail || 'Failed to upload logo');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Error uploading logo');
    } finally {
      setLoading(false);
    }
  };

  const testFbrToken = async () => {
    setLoading(true);
    try {
      const response = await apiPost('/api/tenant/test-fbr-token', { token: fbrToken });
      if (response.ok) {
        const data = response.data;
        setTestResult(data);
        toast.success(data.message);
      } else {
        toast.error(response.data.detail || 'Failed to test FBR token');
      }
    } catch (error) {
      console.error('Error testing FBR token:', error);
      toast.error('Error testing FBR token');
    } finally {
      setLoading(false);
    }
  };

  const getFbrStatusColor = () => {
    switch (fbrTokenStatus) {
      case 'configured': return 'text-green-600';
      case 'not_configured': return 'text-red-600';
      case 'invalid': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  const getFbrStatusIcon = () => {
    switch (fbrTokenStatus) {
      case 'configured': return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'not_configured': return <AlertCircle className="h-5 w-5 text-red-600" />;
      case 'invalid': return <AlertCircle className="h-5 w-5 text-yellow-600" />;
      default: return <AlertCircle className="h-5 w-5 text-gray-600" />;
    }
  };

  const syncFbrData = async () => {
    if (fbrTokenStatus !== 'configured') {
      toast.error('FBR token not configured. Please add your FBR authentication token first.');
      return;
    }

    setSyncingData(true);
    setSyncLogs([]);
    setShowSyncLogs(true);
    
    try {
      const response = await apiPost('/api/fbr/sync/with-token');
      
      if (response.ok) {
        const results = response.data.results || {};
        const logs = [];
        
        // Process sync results into detailed logs
        Object.entries(results).forEach(([table, result]) => {
          // Handle both boolean (old format) and object (new format) results
          if (typeof result === 'boolean') {
            if (result) {
              logs.push({
                type: 'success',
                table: table,
                message: `${table}: Synced successfully (incremental update)`,
                details: { success: true, format: 'boolean' }
              });
            } else {
              logs.push({
                type: 'error',
                table: table,
                message: `${table}: Sync failed`,
                details: { success: false, format: 'boolean' }
              });
            }
          } else if (result && typeof result === 'object') {
            if (result.success) {
              logs.push({
                type: 'success',
                table: table,
                message: `${table}: ${result.added || 0} added, ${result.updated || 0} updated`,
                details: result
              });
            } else {
              logs.push({
                type: 'error',
                table: table,
                message: `${table}: Failed - ${result.error || 'Unknown error'}`,
                details: result
              });
            }
          } else {
            logs.push({
              type: 'error',
              table: table,
              message: `${table}: Unknown result format`,
              details: result
            });
          }
        });
        
        setSyncLogs(logs);
        
        const totalAdded = Object.values(results).reduce((sum, result) => {
          if (typeof result === 'object' && result.added) {
            return sum + result.added;
          }
          return sum;
        }, 0);
        const totalUpdated = Object.values(results).reduce((sum, result) => {
          if (typeof result === 'object' && result.updated) {
            return sum + result.updated;
          }
          return sum;
        }, 0);
        
        const successCount = Object.values(results).filter(result => 
          (typeof result === 'boolean' && result) || 
          (typeof result === 'object' && result.success)
        ).length;
        
        if (totalAdded > 0 || totalUpdated > 0) {
          toast.success(`FBR data synchronized! ${totalAdded} added, ${totalUpdated} updated`);
        } else {
          toast.success(`FBR data synchronized! ${successCount}/${Object.keys(results).length} tables updated successfully`);
        }
        console.log('Sync results:', response.data.results);
      } else {
        setSyncLogs([{
          type: 'error',
          table: 'sync',
          message: `Sync failed: ${response.data.detail || 'Unknown error'}`,
          details: response.data
        }]);
        toast.error('Failed to sync FBR data');
      }
    } catch (error) {
      console.error('Error syncing FBR data:', error);
      setSyncLogs([{
        type: 'error',
        table: 'sync',
        message: `Sync error: ${error.message}`,
        details: error
      }]);
      toast.error('Failed to sync FBR data');
    } finally {
      setSyncingData(false);
    }
  };

  const clearFbrData = async () => {
    if (!window.confirm('Are you sure you want to clear all FBR data? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiDelete('/api/fbr/clear-data');
      if (response.ok) {
        const result = response.data;
        toast.success(`FBR data cleared successfully! ${result.total_cleared} records removed.`);
      } else {
        toast.error(`Clear failed: ${response.data?.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error clearing FBR data:', error);
      toast.error('Failed to clear FBR data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Navigation>
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
              <p className="text-gray-600">Manage your account and business settings</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'profile'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <User className="h-4 w-4 inline mr-2" />
                Profile
              </button>
              <button
                onClick={() => setActiveTab('business')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'business'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Building2 className="h-4 w-4 inline mr-2" />
                Business Settings
              </button>
              <button
                onClick={() => setActiveTab('fbr')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'fbr'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Key className="h-4 w-4 inline mr-2" />
                FBR Integration
              </button>
              <button
                onClick={() => setActiveTab('password')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'password'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Shield className="h-4 w-4 inline mr-2" />
                Password
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <form onSubmit={handleProfileUpdate} className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Profile Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                    <input
                      type="text"
                      value={profileData.name}
                      onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            )}

            {/* Business Settings Tab */}
            {activeTab === 'business' && (
              <form onSubmit={handleBusinessUpdate} className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Business Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
                    <input
                      type="text"
                      value={businessData.business_name}
                      onChange={(e) => setBusinessData({...businessData, business_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Business Type</label>
                    <input
                      type="text"
                      value={businessData.business_type}
                      onChange={(e) => setBusinessData({...businessData, business_type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CNIC</label>
                    <input
                      type="text"
                      value={businessData.tax_number}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                        if (value.length <= 13) { // Limit to 13 digits
                          setBusinessData({...businessData, tax_number: value});
                        }
                      }}
                      maxLength={13}
                      placeholder="13 digits"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">NTN</label>
                    <input
                      type="text"
                      value={businessData.seller_ntn_cnic}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 8);
                        setBusinessData({...businessData, seller_ntn_cnic: value});
                      }}
                      maxLength={8}
                      placeholder="9999997 or ABC12XY"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">7 or 8 characters (alphanumeric)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">STRN</label>
                    <input
                      type="text"
                      value={businessData.seller_strn}
                      onChange={(e) =>
                        setBusinessData({
                          ...businessData,
                          seller_strn: e.target.value.slice(0, 25),
                        })
                      }
                      maxLength={25}
                      placeholder="Optional identifier"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">Max 25 characters</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Province</label>
                    <select
                      value={businessData.seller_province}
                      onChange={(e) => setBusinessData({...businessData, seller_province: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Province</option>
                      {provinces.map((province) => (
                        <option key={province} value={province}>
                          {province}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                    <input
                      type="text"
                      value={businessData.address}
                      onChange={(e) => setBusinessData({
                        ...businessData, 
                        address: e.target.value,
                        seller_address: e.target.value  // Also update seller_address for FBR
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Logo Upload Section */}
                <div className="border-t pt-6">
                  <h4 className="text-md font-medium text-gray-900 mb-4">Company Logo</h4>
                  <div className="flex items-start space-x-6">
                    <div className="flex-shrink-0">
                      {logoPreview ? (
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="h-20 w-20 object-cover rounded-lg border border-gray-300"
                        />
                      ) : currentLogo ? (
                        <img
                          src={currentLogo}
                          alt="Current logo"
                          className="h-20 w-20 object-cover rounded-lg border border-gray-300"
                        />
                      ) : (
                        <div className="h-20 w-20 bg-gray-100 rounded-lg border border-gray-300 flex items-center justify-center">
                          <Building2 className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Upload Logo
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Recommended size: 200x200px. Supported formats: JPG, PNG, GIF
                      </p>
                      {logoFile && (
                        <button
                          type="button"
                          onClick={handleLogoUpload}
                          disabled={loading}
                          className="mt-2 bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm font-medium disabled:opacity-50"
                        >
                          {loading ? 'Uploading...' : 'Upload Logo'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Invoice template */}
                <div className="border-t pt-6">
                  <h4 className="text-md font-medium text-gray-900 mb-1">Invoice Template</h4>
                  <p className="text-xs text-gray-500 mb-4">
                    The layout every invoice prints in. Preview opens your most recent
                    invoice in that template.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {templates.map((tpl) => {
                      const active = selectedTemplate === tpl.id;
                      return (
                        <div
                          key={tpl.id}
                          className={`rounded-lg border p-4 ${active ? 'border-blue-500 ring-1 ring-blue-500 bg-blue-50/40' : 'border-gray-300'}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="font-medium text-gray-900">{tpl.name}</div>
                              <p className="text-xs text-gray-600 mt-1">{tpl.description}</p>
                              <p className="text-xs text-gray-500 mt-1 italic">{tpl.use_when}</p>
                            </div>
                            {active && (
                              <span className="shrink-0 text-xs font-medium text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                                Default
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-3">
                            <button
                              type="button"
                              onClick={() => handlePreviewTemplate(tpl.id)}
                              disabled={previewingTemplate === tpl.id}
                              className="px-3 py-1 rounded text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              {previewingTemplate === tpl.id ? 'Opening...' : 'Preview'}
                            </button>
                            {!active && (
                              <button
                                type="button"
                                onClick={() => handleSelectTemplate(tpl.id)}
                                disabled={savingTemplate}
                                className="px-3 py-1 rounded text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                              >
                                {savingTemplate ? 'Saving...' : 'Set as default'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {selectedTemplate === 'basic' && (
                    <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                      Basic prints no tax, HS code, FBR reference or QR code. An invoice that FBR
                      has already accepted still prints in Classic, so a filed sale keeps the
                      reference the buyer is entitled to.
                    </p>
                  )}
                </div>


                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            )}

            {/* FBR Integration Tab */}
            {activeTab === 'fbr' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">FBR Integration</h3>
                  <div className="flex items-center space-x-2">
                    {getFbrStatusIcon()}
                    <span className={`text-sm font-medium ${getFbrStatusColor()}`}>
                      {fbrTokenStatus === 'configured' ? 'Configured' : 
                       fbrTokenStatus === 'not_configured' ? 'Not Configured' : 
                       fbrTokenStatus === 'invalid' ? 'Invalid Token' : 'Unknown'}
                    </span>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-md">
                  <p className="text-sm text-gray-600 mb-4">
                    Configure your FBR API token to enable automatic invoice submission to FBR. 
                    If no token is configured, the system will use mock responses for testing.
                  </p>
                </div>

                {/* FBR API Mode Toggle */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3">FBR API Mode</h4>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="fbrMode"
                        value="validation"
                        checked={fbrMode === 'validation'}
                        onChange={(e) => handleFbrModeChange(e.target.value)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">Validation Mode</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="fbrMode"
                        value="posting"
                        checked={fbrMode === 'posting'}
                        onChange={(e) => handleFbrModeChange(e.target.value)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">Posting Mode</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {fbrMode === 'validation' 
                      ? 'Validation mode will only validate invoices without submitting them to FBR.'
                      : 'Posting mode will submit invoices directly to FBR for processing.'
                    }
                  </p>
                </div>

                <form onSubmit={handleFbrTokenUpdate} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">FBR API Token</label>
                    <div className="relative">
                      <input
                        type={showToken ? 'text' : 'password'}
                        value={fbrToken}
                        onChange={(e) => setFbrToken(e.target.value)}
                        placeholder="Enter your FBR API token"
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowToken(!showToken)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex space-x-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : 'Save Token'}
                    </button>
                    <button
                      type="button"
                      onClick={testFbrToken}
                      disabled={loading || !fbrToken}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50 flex items-center"
                    >
                      <TestTube className="h-4 w-4 mr-2" />
                      Test Token
                    </button>
                    <button
                      type="button"
                      onClick={handleUnsetToken}
                      disabled={loading || fbrTokenStatus !== 'configured'}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50 flex items-center"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Unset Token
                    </button>
                  </div>
                </form>

                {testResult && (
                  <div className={`p-4 rounded-md ${
                    testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex items-center">
                      {testResult.success ? (
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                      )}
                      <span className={`text-sm font-medium ${
                        testResult.success ? 'text-green-800' : 'text-red-800'
                      }`}>
                        {testResult.message}
                      </span>
                    </div>
                  </div>
                )}

                {/* FBR Data Management Section */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-md font-medium text-gray-900 mb-4">FBR Data Management</h4>
                  
                  {/* Token Status Indicator */}
                  <div className={`mb-4 p-3 rounded-md flex items-center ${
                    fbrTokenStatus === 'configured' 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-yellow-50 border border-yellow-200'
                  }`}>
                    <div className={`flex-shrink-0 ${
                      fbrTokenStatus === 'configured' ? 'text-green-500' : 'text-yellow-500'
                    }`}>
                      {fbrTokenStatus === 'configured' ? (
                        <CheckCircle className="h-5 w-5" />
                      ) : (
                        <AlertTriangle className="h-5 w-5" />
                      )}
                    </div>
                    <div className="ml-3">
                      <p className={`text-sm font-medium ${
                        fbrTokenStatus === 'configured' ? 'text-green-800' : 'text-yellow-800'
                      }`}>
                        {fbrTokenStatus === 'configured' ? 'FBR Token Configured' : 'FBR Token Not Configured'}
                      </p>
                      <p className={`text-xs ${
                        fbrTokenStatus === 'configured' ? 'text-green-700' : 'text-yellow-700'
                      }`}>
                        {fbrTokenStatus === 'configured' 
                          ? 'Ready to sync FBR data' 
                          : 'Please configure your FBR token first'
                        }
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Sync the latest FBR data (UOM, HS Codes, Provinces, etc.) using your configured token.
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        This will update all dropdown options in the product creation form.
                      </p>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={clearFbrData}
                        disabled={loading}
                        className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        title="Clear all FBR data from database"
                      >
                        {loading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Clearing...
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 mr-2" />
                            Clear FBR Data
                          </>
                        )}
                      </button>
                      <button
                        onClick={syncFbrData}
                        disabled={syncingData || fbrTokenStatus !== 'configured'}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        {syncingData ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Syncing...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Sync FBR Data
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Sync Logs Section */}
                {showSyncLogs && syncLogs.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900">Sync Logs</h3>
                      <button
                        onClick={() => setShowSyncLogs(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <XCircle className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                      <div className="space-y-3">
                        {syncLogs.map((log, index) => (
                          <div
                            key={index}
                            className={`flex items-start space-x-3 p-3 rounded-md ${
                              log.type === 'success' 
                                ? 'bg-green-50 border border-green-200' 
                                : 'bg-red-50 border border-red-200'
                            }`}
                          >
                            <div className={`flex-shrink-0 ${
                              log.type === 'success' ? 'text-green-500' : 'text-red-500'
                            }`}>
                              {log.type === 'success' ? (
                                <CheckCircle className="h-5 w-5" />
                              ) : (
                                <XCircle className="h-5 w-5" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className={`text-sm font-medium ${
                                  log.type === 'success' ? 'text-green-800' : 'text-red-800'
                                }`}>
                                  {log.table.toUpperCase()}
                                </p>
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  log.type === 'success' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {log.type}
                                </span>
                              </div>
                              <p className={`text-sm mt-1 ${
                                log.type === 'success' ? 'text-green-700' : 'text-red-700'
                              }`}>
                                {log.message}
                              </p>
                              {log.details && (
                                <details className="mt-2">
                                  <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-800">
                                    View Details
                                  </summary>
                                  <pre className="mt-2 text-xs bg-white p-2 rounded border overflow-x-auto">
                                    {JSON.stringify(log.details, null, 2)}
                                  </pre>
                                </details>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Password Tab */}
            {activeTab === 'password' && (
              <form onSubmit={handlePasswordChange} className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Change Password</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                    <input
                      type="password"
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                    <input
                      type="password"
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                    <input
                      type="password"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50"
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
      </div>
    </Navigation>
  );
};

export default Settings; 